/* eslint-disable react/jsx-props-no-spreading */
import './Accordion.stories.scss';
import './Accordion.scss';

import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReactComponent as ArrowIcon } from '@/common/assets/icons/arrow-top.svg';
import { ReactComponent as LessIcon } from '@/common/assets/icons/less.svg';
import { ReactComponent as PlusIcon } from '@/common/assets/icons/plus.svg';

const Accordion = ({
  accordionLabel,
  children,
  className,
  context,
  isOpen,
  icon,
  id,
  maxHeight,
  overflow,
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(isOpen);
  const contentRef = useRef(null);
  const classNames = useMemo(
    () =>
      ['accordion', isOpenInternal ? 'accordion--open' : null, className]
        .filter((val) => val)
        .join(' '),
    [className, isOpenInternal],
  );
  const isValidHeight = useCallback((height) => {
    if (
      /^(auto|fit-content|[+-]?\d*\.?\d+(px|em|rem|vh|vw|vmin|vmax))$/.test(
        height,
      )
    ) {
      return height;
    }
    return false;
  }, []);

  /**
   * Update content max height depends on scrollHeight value and opening state
   * @param {boolean} open
   */
  const updateContentMaxHeight = useCallback(
    (open) => {
      if (maxHeight) {
        contentRef.current.style.setProperty(
          '--accordion-content-max-height--open',
          open
            ? isValidHeight(maxHeight) || `${contentRef.current.scrollHeight}px`
            : 0,
        );
      }
      if (overflow) {
        contentRef.current.style.setProperty(
          '--accordion-content-overflow--open',
          open ? overflow : 'hidden',
        );
      }
    },
    [maxHeight, isValidHeight, overflow],
  );

  /**
   * Toggle open/close accordion
   */
  const toggleAccordion = useCallback(() => {
    updateContentMaxHeight(!isOpenInternal);
    setIsOpenInternal(!isOpenInternal);
  }, [isOpenInternal, updateContentMaxHeight]);

  const conditionalProps = useMemo(
    () => ({
      ...(id ? { id } : {}),
    }),
    [id],
  );

  useEffect(() => {
    updateContentMaxHeight(isOpen);
    setIsOpenInternal(isOpen);
  }, [isOpen, updateContentMaxHeight]);

  return (
    <div
      className={classNames}
      {...conditionalProps}
      data-qa-id={context || 'accordion'}
    >
      <div
        className="accordion__title"
        data-qa-id="title"
        onClick={toggleAccordion}
        onKeyPress={toggleAccordion}
        role="button"
        tabIndex="0"
      >
        {accordionLabel}
        {/* eslint-disable-next-line no-nested-ternary */}
        {icon === 'plus' ? (
          isOpenInternal ? (
            <LessIcon className="accordion__icon" data-qa-id="less" />
          ) : (
            <PlusIcon className="accordion__icon" data-qa-id="more" />
          )
        ) : (
          <ArrowIcon
            className="accordion__icon accordion__icon-arrow"
            data-qa-id="arrow"
          />
        )}
      </div>
      <div className="accordion__content" ref={contentRef} data-qa-id="content">
        {children}
      </div>
    </div>
  );
};

Accordion.defaultProps = {
  className: '',
  context: null,
  icon: 'plus',
  id: '',
  isOpen: true,
  maxHeight: null,
  overflow: null,
};

Accordion.propTypes = {
  accordionLabel: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
  context: PropTypes.string,
  icon: PropTypes.oneOf(['plus', 'arrow']),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  maxHeight: PropTypes.string,
  overflow: PropTypes.oneOf(['auto', 'hidden', 'scroll', 'visible']),
};

export default Accordion;
